import React, { useState, useEffect, useRef } from 'react';
import SockJS from 'sockjs-client';
import { Stomp } from '@stomp/stompjs';
import styles from './WebsocketTestTool.module.css';

const WebsocketTestTool = () => {
  // State management
  const [connectionConfig, setConnectionConfig] = useState({
    serverUrl: `${process.env.REACT_APP_ORIGIN}api/ws`,
    storeId: '4000',
    deviceCode: '77f7e3c81d7685e4',
  });
  
  const [connected, setConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [logMessages, setLogMessages] = useState([]);
  const [messageConfig, setMessageConfig] = useState({
    type: 'order',
    payload: JSON.stringify({
      code: "order",
      roomId: "4000",
      userId: "",
      content: [
        {
          deviceId: "dae002",
          items: [
            {productCode: 1, quantity: 1},
            {productCode: 2, quantity: 1}
          ]
        }
      ],
      deviceCode: "77f7e3c81d7685e4",
      cartList: null
    }, null, 2),
  });
  
  const [loadTestConfig, setLoadTestConfig] = useState({
    requestCount: 10,
    requestInterval: 1000,
    deviceIdPrefix: 'dae00',
    deviceIdStart: 2,
    deviceIds: ['dae002'], // 변경: 여러 deviceId를
    newDeviceId: '', // 추가: 새 deviceId 입력을 위한 필드
  });
  
  const [concurrencyTestConfig, setConcurrencyTestConfig] = useState({
    concurrentRequests: 5,
    batchCount: 3,
    batchInterval: 5000,
    deviceCodes: ["77f7e3c81d7685e4", "6b20a759bbef03b7"], // 디바이스 코드 배열
    deviceIds: ['dae002'], // 변경: 여러 deviceId 배열
    newDeviceId: '', // 추가: 새 deviceId 입력을 위한 필드
  });
  
  const [testStats, setTestStats] = useState({
    sent: 0,
    successful: 0,
    failed: 0,
    responseTimes: [],
    batches: 0,
  });
  
  // Refs
  const stompClientRef = useRef(null);
  const messageResponsesRef = useRef({});
  const messageCounterRef = useRef(0);
  const loadTestIntervalRef = useRef(null);
  const concurrencyTestIntervalRef = useRef(null);
  const outputRef = useRef(null);
  
  // Effect for auto-scrolling logs
  useEffect(() => {
    if (outputRef.current) {
      outputRef.current.scrollTop = outputRef.current.scrollHeight;
    }
  }, [logMessages]);
  
  // Functions for connection management
  const connect = () => {
    if (connecting || connected) return;
    
    setConnecting(true);
    log(`서버에 연결 시도 중: ${connectionConfig.serverUrl}`, 'connection');
    
    try {
      const socket = new SockJS(connectionConfig.serverUrl);
      const client = Stomp.over(socket);
      
      // Limit STOMP debug logs
      client.debug = msg => {
        if (!msg.includes('<<heart') && !msg.includes('>>CONNECT')) {
          log(msg, 'stomp');
        }
      };
      
      client.connect({}, 
        frame => {
          log(`STOMP 연결 성공: ${frame}`, 'connection');
          setConnected(true);
          setConnecting(false);
          stompClientRef.current = client;
          
          // Subscribe to the channel
          client.subscribe(`/chatroom/${connectionConfig.storeId}`, response => {
            handleResponse(response);
          });
        },
        error => {
          log(`연결 오류: ${error}`, 'error');
          setConnected(false);
          setConnecting(false);
          stompClientRef.current = null;
        }
      );
    } catch (error) {
      log(`연결 초기화 오류: ${error.message}`, 'error');
      setConnected(false);
      setConnecting(false);
    }
  };
  
  const disconnect = () => {
    if (!stompClientRef.current) return;
    
    stopLoadTest();
    stopConcurrencyTest();
    
    try {
      stompClientRef.current.disconnect(() => {
        log('웹소켓 연결 종료됨', 'connection');
        setConnected(false);
        stompClientRef.current = null;
      });
    } catch (error) {
      log(`연결 종료 오류: ${error.message}`, 'error');
      stompClientRef.current = null;
      setConnected(false);
    }
  };
  
  // Handle WebSocket responses
  const handleResponse = (response) => {
    try {
      const responseData = JSON.parse(response.body);
      log(`수신된 메시지: ${response.body}`, 'received');
      
      // Track response time if message ID is present
      if (responseData.messageId) {
        const sentMessage = messageResponsesRef.current[responseData.messageId];
        if (sentMessage) {
          const responseTime = Date.now() - sentMessage.timestamp;
          log(`응답 시간 (${responseData.messageId}): ${responseTime}ms`, 'perf');
          
          setTestStats(prevStats => {
            const newResponseTimes = [...prevStats.responseTimes, responseTime];
            
            if (responseData.error) {
              return {...prevStats, failed: prevStats.failed + 1, responseTimes: newResponseTimes};
            } else {
              return {...prevStats, successful: prevStats.successful + 1, responseTimes: newResponseTimes};
            }
          });
        }
      }
      
      // Process specific message types
      if (responseData.code === 'updateRailStatus') {
        log(`레일 상태 업데이트: ${responseData.content.railActive ? '활성' : '비활성'}, 디바이스: ${responseData.content.deviceCode || 'none'}`, 'status');
      } else if (responseData.code === 'railCompletionConfirmed') {
        log(`레일 작업 완료 확인: ${responseData.content.message}, 디바이스: ${responseData.content.deviceCode}`, 'success');
      } else if (responseData.code === 'dispenseError') {
        log(`배출 오류: ${responseData.content}`, 'error');
      } else if (responseData.code === 'executeDispense') {
        log(`배출 명령 수신: 키오스크 ID=${Object.keys(responseData.content.allKiosks).join(', ')}`, 'command');
      }
      
    } catch (error) {
      log(`응답 처리 오류: ${error.message}`, 'error');
    }
  };
  
  // Message sending
  const sendCustomMessage = () => {
    if (!stompClientRef.current || !stompClientRef.current.connected) {
      log('연결되지 않았습니다!', 'error');
      return;
    }
    
    try {
      const messageType = messageConfig.type;
      let messagePayload = messageConfig.payload;
      
      try {
        // Parse and prepare the message
        let message = JSON.parse(messagePayload);
        
        // Set necessary fields
        message.code = message.code || messageType;
        message.roomId = message.roomId || connectionConfig.storeId;
        message.messageId = `msg-${Date.now()}-${++messageCounterRef.current}`;
        
        if (!message.deviceCode) {
          message.deviceCode = connectionConfig.deviceCode;
        }
        
        // Record send time for this message
        messageResponsesRef.current[message.messageId] = {
          timestamp: Date.now(),
          message: message
        };
        
        log(`전송: ${JSON.stringify(message)}`, 'sent');
        console.log("전송 메시지 (단일 테스트):", message);
        
        // Send to appropriate endpoint based on message type
        log(`메시지 전송 시작: 타입=${message.code}`, 'debug');
        
        switch (messageType) {
          case 'dispense':
            log(`엔드포인트: /app/dispense/${connectionConfig.storeId}`, 'debug');
            stompClientRef.current.send(`/app/dispense/${connectionConfig.storeId}`, {}, JSON.stringify(message));
            log("배출 요청 메시지 전송 완료", 'success');
            break;
          case 'railCompleted':
            log(`엔드포인트: /app/railCompleted/${connectionConfig.storeId}`, 'debug');
            stompClientRef.current.send(`/app/railCompleted/${connectionConfig.storeId}`, {}, JSON.stringify(message));
            log("레일 완료 메시지 전송 완료", 'success');
            break;
          case 'dispenseResult':
            log(`엔드포인트: /app/dispenseResult/${connectionConfig.storeId}`, 'debug');
            stompClientRef.current.send(`/app/dispenseResult/${connectionConfig.storeId}`, {}, JSON.stringify(message));
            log("배출 결과 메시지 전송 완료", 'success');
            break;
          default:
            log(`엔드포인트: /app/sendMessage/${connectionConfig.storeId}`, 'debug');
            stompClientRef.current.send(`/app/sendMessage/${connectionConfig.storeId}`, {}, JSON.stringify(message));
            log("일반 메시지 전송 완료", 'success');
            break;
        }
        
        setTestStats(prev => ({...prev, sent: prev.sent + 1}));
        
      } catch (parseError) {
        log(`JSON 파싱 오류: ${parseError.message}`, 'error');
      }
    } catch (error) {
      log(`메시지 전송 오류: ${error.message}`, 'error');
      console.error("전송 오류 상세:", error);
    }
  };
  
  // Generate test messages with multiple deviceIds support
  const generateTestMessage = (index, isLoadTest = true) => {
    if (isLoadTest) {
      // 부하 테스트: 하나의 발신 deviceCode만 사용, 여러 대상 deviceId 순환
      const deviceIds = loadTestConfig.deviceIds;
      const deviceId = deviceIds[index % deviceIds.length];
      
      return {
        code: "dispense",
        roomId: connectionConfig.storeId,
        content: [
          {
            deviceId: deviceId,
            items: [
              {productCode: 1, quantity: 1},
              {productCode: 2, quantity: 1}
            ]
          }
        ],
        deviceCode: connectionConfig.deviceCode, // 항상 같은 발신 디바이스 코드
        messageId: `load-${Date.now()}-${index}`
      };
    } else {
      // 동시성 테스트: 각 요청마다 다른 deviceCode를 사용 (실제 여러 키오스크 시뮬레이션)
      const deviceIds = concurrencyTestConfig.deviceIds;
      const deviceCodes = concurrencyTestConfig.deviceCodes;
      
      // 타겟 deviceId와 발신 deviceCode 모두 순환
      const deviceId = deviceIds[index % deviceIds.length];
      const deviceCode = deviceCodes[index % deviceCodes.length];
      
      return {
        code: "dispense",
        roomId: connectionConfig.storeId,
        content: [
          {
            deviceId: deviceId,
            items: [
              {productCode: 1, quantity: 1},
              {productCode: 2, quantity: 1}
            ]
          }
        ],
        deviceCode: deviceCode, // 다른 발신 디바이스 코드 사용
        messageId: `conc-${Date.now()}-${index}`
      };
    }
  };
  
  // Load testing
  const startLoadTest = () => {
    if (!stompClientRef.current || !stompClientRef.current.connected) {
      log('웹소켓 연결이 필요합니다!', 'error');
      return;
    }
    
    if (loadTestConfig.deviceIds.length === 0) {
      log('최소한 하나의 디바이스 ID가 필요합니다!', 'error');
      return;
    }
    
    stopLoadTest();
    resetStats();
    
    log(`부하 테스트 시작: ${loadTestConfig.requestCount}개 요청, ${loadTestConfig.requestInterval}ms 간격, ${loadTestConfig.deviceIds.length}개 디바이스 ID`, 'test');
    log(`사용 디바이스 ID: ${loadTestConfig.deviceIds.join(', ')}`, 'test');
    
    let currentRequest = 0;
    
    loadTestIntervalRef.current = setInterval(() => {
      if (currentRequest >= loadTestConfig.requestCount) {
        stopLoadTest();
        log(`부하 테스트 완료: ${currentRequest}개 요청 전송됨`, 'test');
        return;
      }
      
      const message = generateTestMessage(currentRequest, true);
      messageResponsesRef.current[message.messageId] = {
        timestamp: Date.now(),
        message: message
      };
      
      log(`요청 ${currentRequest + 1}/${loadTestConfig.requestCount} 전송 (디바이스: ${message.content[0].deviceId})`, 'test');
      
      stompClientRef.current.send(`/app/dispense/${connectionConfig.storeId}`, {}, JSON.stringify(message));
      
      setTestStats(prev => ({...prev, sent: prev.sent + 1}));
      currentRequest++;
      
    }, loadTestConfig.requestInterval);
  };
  
  const stopLoadTest = () => {
    if (loadTestIntervalRef.current) {
      clearInterval(loadTestIntervalRef.current);
      loadTestIntervalRef.current = null;
      log('부하 테스트 중지됨', 'test');
    }
  };
  
  // Concurrency testing
  const startConcurrencyTest = () => {
    if (!stompClientRef.current || !stompClientRef.current.connected) {
      log('웹소켓 연결이 필요합니다!', 'error');
      return;
    }
    
    if (concurrencyTestConfig.deviceIds.length === 0) {
      log('최소한 하나의 디바이스 ID가 필요합니다!', 'error');
      return;
    }
    
    if (concurrencyTestConfig.deviceCodes.length === 0) {
      log('최소한 하나의 디바이스 코드가 필요합니다!', 'error');
      return;
    }
    
    stopConcurrencyTest();
    resetStats();
    
    log(`동시성 테스트 시작: 배치당 ${concurrencyTestConfig.concurrentRequests}개 요청, 총 ${concurrencyTestConfig.batchCount}개 배치, ${concurrencyTestConfig.batchInterval}ms 간격`, 'test');
    log(`사용 디바이스 ID: ${concurrencyTestConfig.deviceIds.join(', ')}`, 'test');
    log(`사용 디바이스 코드: ${concurrencyTestConfig.deviceCodes.join(', ')}`, 'test');
    
    let currentBatch = 0;
    
    concurrencyTestIntervalRef.current = setInterval(() => {
      if (currentBatch >= concurrencyTestConfig.batchCount) {
        stopConcurrencyTest();
        log(`동시성 테스트 완료: ${currentBatch}개 배치 (총 ${currentBatch * concurrencyTestConfig.concurrentRequests}개 요청) 전송됨`, 'test');
        return;
      }
      
      log(`배치 ${currentBatch + 1}/${concurrencyTestConfig.batchCount} 전송 (${concurrencyTestConfig.concurrentRequests}개 요청)`, 'test');
      
      // Send multiple requests simultaneously
      for (let i = 0; i < concurrencyTestConfig.concurrentRequests; i++) {
        const index = currentBatch * concurrencyTestConfig.concurrentRequests + i;
        const message = generateTestMessage(index, false);
        
        messageResponsesRef.current[message.messageId] = {
          timestamp: Date.now(),
          message: message,
          batch: currentBatch
        };
        
        // 디바이스 코드 정보를 로그에 추가
        log(`요청 전송: messageId=${message.messageId}, deviceId=${message.content[0].deviceId}, deviceCode=${message.deviceCode}`, 'debug');
        
        stompClientRef.current.send(`/app/dispense/${connectionConfig.storeId}`, {}, JSON.stringify(message));
      }
      
      setTestStats(prev => ({
        ...prev, 
        sent: prev.sent + concurrencyTestConfig.concurrentRequests,
        batches: prev.batches + 1
      }));
      
      currentBatch++;
      
    }, concurrencyTestConfig.batchInterval);
  };
  
  const stopConcurrencyTest = () => {
    if (concurrencyTestIntervalRef.current) {
      clearInterval(concurrencyTestIntervalRef.current);
      concurrencyTestIntervalRef.current = null;
      log('동시성 테스트 중지됨', 'test');
    }
  };
  
  // Handler for adding device IDs
  const addDeviceId = (isLoadTest) => {
    if (isLoadTest) {
      const newId = loadTestConfig.newDeviceId.trim();
      if (newId && !loadTestConfig.deviceIds.includes(newId)) {
        setLoadTestConfig({
          ...loadTestConfig,
          deviceIds: [...loadTestConfig.deviceIds, newId],
          newDeviceId: ''
        });
        log(`부하 테스트에 디바이스 ID 추가됨: ${newId}`, 'info');
      }
    } else {
      const newId = concurrencyTestConfig.newDeviceId.trim();
      if (newId && !concurrencyTestConfig.deviceIds.includes(newId)) {
        setConcurrencyTestConfig({
          ...concurrencyTestConfig,
          deviceIds: [...concurrencyTestConfig.deviceIds, newId],
          newDeviceId: ''
        });
        log(`동시성 테스트에 디바이스 ID 추가됨: ${newId}`, 'info');
      }
    }
  };
  
  // Handler for removing device IDs
  const removeDeviceId = (isLoadTest, deviceId) => {
    if (isLoadTest) {
      setLoadTestConfig({
        ...loadTestConfig,
        deviceIds: loadTestConfig.deviceIds.filter(id => id !== deviceId)
      });
      log(`부하 테스트에서 디바이스 ID 제거됨: ${deviceId}`, 'info');
    } else {
      setConcurrencyTestConfig({
        ...concurrencyTestConfig,
        deviceIds: concurrencyTestConfig.deviceIds.filter(id => id !== deviceId)
      });
      log(`동시성 테스트에서 디바이스 ID 제거됨: ${deviceId}`, 'info');
    }
  };
  
  // Utility functions
  const log = (message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    const formattedMessage = `[${timestamp}] [${type.toUpperCase()}] ${message}`;
    
    setLogMessages(prev => [...prev, { message: formattedMessage, type }]);
  };
  
  const clearLogs = () => {
    setLogMessages([]);
  };
  
  const resetStats = () => {
    setTestStats({
      sent: 0,
      successful: 0,
      failed: 0,
      responseTimes: [],
      batches: 0
    });
  };
  
  // Update message template based on type
  const updateMessageTemplate = (type) => {
    let template = '';
    
    switch(type) {
      case 'order':
        template = JSON.stringify({
          code: "order",
          roomId: connectionConfig.storeId,
          userId: "",
          content: [
            {
              deviceId: "dae002",
              items: [
                {productCode: 1, quantity: 1},
                {productCode: 2, quantity: 1}
              ]
            }
          ],
          deviceCode: connectionConfig.deviceCode,
          cartList: null
        }, null, 2);
        break;
      case 'dispense':
        template = JSON.stringify({
          code: "dispense",
          roomId: connectionConfig.storeId,
          content: [
            {
              deviceId: "dae002",
              items: [
                {productCode: 1, quantity: 1},
                {productCode: 2, quantity: 1}
              ]
            }
          ],
          deviceCode: connectionConfig.deviceCode
        }, null, 2);
        break;
      case 'railCompleted':
        template = JSON.stringify({
          code: "railCompleted",
          roomId: connectionConfig.storeId,
          deviceCode: connectionConfig.deviceCode,
          content: {
            deviceCode: connectionConfig.deviceCode,
            timestamp: Date.now()
          }
        }, null, 2);
        break;
      case 'dispenseResult':
        template = JSON.stringify({
          code: "dispenseResult",
          roomId: connectionConfig.storeId,
          success: true,
          deviceCode: connectionConfig.deviceCode,
          details: "성공적으로 배출됨"
        }, null, 2);
        break;
      default:
        template = messageConfig.payload;
    }
    
    setMessageConfig({
      type,
      payload: template
    });
  };
  
  // Calculate average response time
  const getAverageResponseTime = () => {
    if (testStats.responseTimes.length === 0) return 0;
    const sum = testStats.responseTimes.reduce((a, b) => a + b, 0);
    return (sum / testStats.responseTimes.length).toFixed(2);
  };
  
  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (stompClientRef.current) {
        disconnect();
      }
      
      if (loadTestIntervalRef.current) {
        clearInterval(loadTestIntervalRef.current);
      }
      
      if (concurrencyTestIntervalRef.current) {
        clearInterval(concurrencyTestIntervalRef.current);
      }
    };
  }, []);
  
  // Render component
  return (
    <div className={styles.websocketTestTool}>
      <h1>키오스크 웹소켓 테스트 도구</h1>
      
      <div className={styles.container}>
        <div className={styles.panel}>
          <h2>연결 설정</h2>
          <div className={styles.inputGroup}>
            <label htmlFor="serverUrl">서버 URL:</label>
            <input
              type="text"
              id="serverUrl"
              className={styles.input}
              value={connectionConfig.serverUrl}
              onChange={(e) => setConnectionConfig({...connectionConfig, serverUrl: e.target.value})}
            />
          </div>
          <div className={styles.inputGroup}>
            <label htmlFor="storeId">스토어 ID:</label>
            <input
              type="text"
              id="storeId"
              className={styles.input}
              value={connectionConfig.storeId}
              onChange={(e) => setConnectionConfig({...connectionConfig, storeId: e.target.value})}
            />
          </div>
          <div className={styles.inputGroup}>
            <label htmlFor="deviceCode">디바이스 코드:</label>
            <input
              type="text"
              id="deviceCode"
              className={styles.input}
              value={connectionConfig.deviceCode}
              onChange={(e) => setConnectionConfig({...connectionConfig, deviceCode: e.target.value})}
            />
          </div>
          <div 
            id="connectionStatus" 
            className={`${styles.connectionStatus} ${connected ? styles.connected : styles.disconnected}`}
          >
            상태: {connected ? '연결됨' : '연결 끊김'}
          </div>
          <button 
            className={styles.button}
            onClick={connect} 
            disabled={connected || connecting}
          >
            연결
          </button>
          <button 
            className={styles.button}
            onClick={disconnect} 
            disabled={!connected}
          >
            연결 해제
          </button>
        </div>
        
        <div className={styles.panel}>
          <h2>단일 테스트</h2>
          <div className={styles.inputGroup}>
            <label htmlFor="messageType">메시지 타입:</label>
            <select 
              id="messageType" 
              className={styles.select}
              value={messageConfig.type}
              onChange={(e) => updateMessageTemplate(e.target.value)}
            >
              <option value="order">주문 (order)</option>
              <option value="dispense">배출 (dispense)</option>
              <option value="railCompleted">레일 완료 (railCompleted)</option>
              <option value="dispenseResult">배출 결과 (dispenseResult)</option>
            </select>
          </div>
          <div className={styles.inputGroup}>
            <label htmlFor="messagePayload">메시지 내용 (JSON):</label>
            <textarea
              id="messagePayload"
              className={styles.textarea}
              rows="8"
              value={messageConfig.payload}
              onChange={(e) => setMessageConfig({...messageConfig, payload: e.target.value})}
            />
          </div>
          <button 
            className={styles.button}
            onClick={sendCustomMessage}
          >
            메시지 전송
          </button>
        </div>
      </div>
      
      <div className={styles.container}>
        <div className={styles.panel}>
          <h2>부하 테스트</h2>
          <div className={styles.inputGroup}>
            <label htmlFor="requestCount">요청 수:</label>
            <input
              type="number"
              id="requestCount"
              className={styles.input}
              value={loadTestConfig.requestCount}
              min="1"
              max="200"
              onChange={(e) => setLoadTestConfig({...loadTestConfig, requestCount: parseInt(e.target.value)})}
            />
          </div>
          <div className={styles.inputGroup}>
            <label htmlFor="requestInterval">요청 간격 (ms):</label>
            <input
              type="number"
              id="requestInterval"
              className={styles.input}
              value={loadTestConfig.requestInterval}
              min="0"
              onChange={(e) => setLoadTestConfig({...loadTestConfig, requestInterval: parseInt(e.target.value)})}
            />
          </div>
          
          {/* 디바이스 ID 목록 관리 UI */}
          <div className={styles.inputGroup}>
            <label htmlFor="loadDeviceIds">디바이스 ID 목록:</label>
            <div className={styles.deviceIdList}>
              {loadTestConfig.deviceIds.map((deviceId, index) => (
                <div key={index} className={styles.deviceIdItem}>
                  <span>{deviceId}</span>
                  <button 
                    className={styles.removeBtn}
                    onClick={() => removeDeviceId(true, deviceId)}
                    title="제거"
                  >
                    X
                  </button>
                </div>
              ))}
            </div>
          </div>
          
          <div className={styles.inputGroup}>
            <label htmlFor="newLoadDeviceId">디바이스 ID 추가:</label>
            <div className={styles.addDeviceId}>
              <input
                type="text"
                id="newLoadDeviceId"
                className={styles.input}
                value={loadTestConfig.newDeviceId}
                onChange={(e) => setLoadTestConfig({...loadTestConfig, newDeviceId: e.target.value})}
                placeholder="예: dae002"
              />
              <button 
                className={styles.button}
                onClick={() => addDeviceId(true)}
              >
                추가
              </button>
            </div>
          </div>
          
          <button 
            className={styles.button}
            onClick={startLoadTest} 
            disabled={!connected || loadTestIntervalRef.current !== null || loadTestConfig.deviceIds.length === 0}
          >
            부하 테스트 시작
          </button>
          <button 
            className={styles.button}
            onClick={stopLoadTest} 
            disabled={!connected || loadTestIntervalRef.current === null}
          >
            부하 테스트 중지
          </button>
          
          <div className={styles.stats}>
            <div>전송된 요청: <span>{testStats.sent}</span></div>
            <div>성공적인 응답: <span>{testStats.successful}</span></div>
            <div>실패한 응답: <span>{testStats.failed}</span></div>
            <div>평균 응답 시간: <span>{getAverageResponseTime()}</span> ms</div>
          </div>
        </div>
        
        <div className={styles.panel}>
          <h2>동시성 테스트</h2>
          <div className={styles.inputGroup}>
            <label htmlFor="concurrentRequests">동시 요청 수:</label>
            <input
              type="number"
              id="concurrentRequests"
              className={styles.input}
              value={concurrencyTestConfig.concurrentRequests}
              min="1"
              max="50"
              onChange={(e) => setConcurrencyTestConfig({...concurrencyTestConfig, concurrentRequests: parseInt(e.target.value)})}
            />
          </div>
          <div className={styles.inputGroup}>
            <label htmlFor="batchCount">배치 수:</label>
            <input
              type="number"
              id="batchCount"
              className={styles.input}
              value={concurrencyTestConfig.batchCount}
              min="1"
              max="10"
              onChange={(e) => setConcurrencyTestConfig({...concurrencyTestConfig, batchCount: parseInt(e.target.value)})}
            />
          </div>
          
          {/* 디바이스 ID 목록 관리 UI */}
          <div className={styles.inputGroup}>
            <label htmlFor="concDeviceIds">디바이스 ID 목록:</label>
            <div className={styles.deviceIdList}>
              {concurrencyTestConfig.deviceIds.map((deviceId, index) => (
                <div key={index} className={styles.deviceIdItem}>
                  <span>{deviceId}</span>
                  <button 
                    className={styles.removeBtn}
                    onClick={() => removeDeviceId(false, deviceId)}
                    title="제거"
                  >
                    X
                  </button>
                </div>
              ))}
            </div>
          </div>
          
          <div className={styles.inputGroup}>
            <label htmlFor="newConcDeviceId">디바이스 ID 추가:</label>
            <div className={styles.addDeviceId}>
              <input
                type="text"
                id="newConcDeviceId"
                className={styles.input}
                value={concurrencyTestConfig.newDeviceId}
                onChange={(e) => setConcurrencyTestConfig({...concurrencyTestConfig, newDeviceId: e.target.value})}
                placeholder="예: dae002"
              />
              <button 
                className={styles.button}
                onClick={() => addDeviceId(false)}
              >
                추가
              </button>
            </div>
          </div>
          
          <div className={styles.inputGroup}>
            <label htmlFor="deviceCodes">디바이스 코드 (쉼표로 구분):</label>
            <input
              type="text"
              id="deviceCodes"
              className={styles.input}
              value={concurrencyTestConfig.deviceCodes.join(',')}
              onChange={(e) => setConcurrencyTestConfig({
                ...concurrencyTestConfig, 
                deviceCodes: e.target.value.split(',').map(code => code.trim()).filter(code => code)
              })}
            />
            <div className={styles.note}>예: 77f7e3c81d7685e4,6b20a759bbef03b7</div>
          </div>
          <button 
            className={styles.button}
            onClick={startConcurrencyTest} 
            disabled={!connected || concurrencyTestIntervalRef.current !== null || concurrencyTestConfig.deviceIds.length === 0 || concurrencyTestConfig.deviceCodes.length === 0}
          >
            동시성 테스트 시작
          </button>
          <button 
            className={styles.button}
            onClick={stopConcurrencyTest} 
            disabled={!connected || concurrencyTestIntervalRef.current === null}
          >
            동시성 테스트 중지
          </button>
          
          <div className={styles.stats}>
            <div>완료된 배치: <span>{testStats.batches}</span></div>
            <div>동시 요청 통계: <span>{testStats.batches > 0 ? `배치당 ${concurrencyTestConfig.concurrentRequests}개 요청` : '-'}</span></div>
          </div>
        </div>
      </div>
      
      <div className={styles.container}>
        <div className={styles.panel}>
          <h2>로그 출력</h2>
          <button 
            className={styles.button}
            onClick={clearLogs}
          >
            로그 지우기
          </button>
          <div id="output" className={styles.output} ref={outputRef}>
            {logMessages.map((log, index) => (
              <div key={index} className={`${styles.logEntry} ${styles[`log${log.type.charAt(0).toUpperCase() + log.type.slice(1)}`]}`}>
                {log.message}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WebsocketTestTool;